import { createContext, useCallback, useContext } from 'react';

import { useDatasheetPopoverStore } from '#components/datasheet-popover/datasheet-popover-store.js';

import type { DatasheetPopoverTarget } from '#components/datasheet-popover/datasheet-popover-store.js';

/** Shows a datasheet at the page, and with the bounding boxes, the target asks for. */
export type ShowInDatasheet = (target: DatasheetPopoverTarget) => void;

/** How the evidence cards beneath it show a locator in the datasheet; the app's datasheet popover when not provided. */
export const ShowInDatasheetContext = createContext<ShowInDatasheet | null>(null);

/**
 * How an evidence card shows its locator in the datasheet: as the nearest {@link ShowInDatasheetContext} says, or by
 * opening the app's datasheet popover when none does.
 *
 * @returns The function that shows a locator.
 */
export function useShowInDatasheet(): ShowInDatasheet {
	const open = useDatasheetPopoverStore((state) => state.open);
	return useContext(ShowInDatasheetContext) ?? open;
}

/**
 * Opens the app's datasheet popover on a locator of a package's evidence, marking the datasheet as opened from that
 * package's evidence so the window shows it beside the datasheet. Without a package, the popover opens on the locator
 * alone, as it does everywhere else.
 *
 * @param packageId - The package whose evidence the datasheet window may show, or undefined when it may show none.
 * @returns The function that shows a locator.
 */
export function useShowPackageEvidenceInDatasheet(packageId: string | undefined): ShowInDatasheet {
	const open = useDatasheetPopoverStore((state) => state.open);
	return useCallback(
		(target: DatasheetPopoverTarget) => {
			open(packageId === undefined ? target : { ...target, evidence: { packageId } });
		},
		[open, packageId],
	);
}
